
import { DataOneUtils } from "./utils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface EquipmentFeaturesProps {
  data?: any;
  selectedStyle?: any;
  selectedStyleIndex?: number;
}

export const EquipmentFeatures = ({ data, selectedStyle, selectedStyleIndex }: EquipmentFeaturesProps) => {
  // Extract equipment features based on selection or from data if not directly provided
  let equipment = {};
  
  if (selectedStyle) {
    // Try to get equipment directly from the selected style
    if (selectedStyle.equipment?.equipment_group) {
      const groups = selectedStyle.equipment.equipment_group;
      const equipmentByCategory: Record<string, any[]> = {};
      
      const processGroups = (groups: any[]) => {
        groups.forEach(group => {
          if (group?.equipment_category && group?.equipment_items?.equipment_item) {
            const category = group.equipment_category;
            const items = Array.isArray(group.equipment_items.equipment_item)
              ? group.equipment_items.equipment_item
              : [group.equipment_items.equipment_item];
            
            if (!equipmentByCategory[category]) {
              equipmentByCategory[category] = [];
            }
            
            equipmentByCategory[category].push(...items);
          }
        });
      };
      
      if (Array.isArray(groups)) {
        processGroups(groups);
      } else {
        processGroups([groups]);
      }
      
      equipment = equipmentByCategory;
    }
  } else {
    // Fall back to utility function for getting equipment from overall data
    equipment = DataOneUtils.extractEquipmentFeatures(data) || {};
  }
  
  // Group equipment by main categories for better organization
  const groupedEquipment = DataOneUtils.groupEquipmentByCategory(equipment);
  
  if (Object.keys(groupedEquipment).length === 0) {
    return (
      <div className="text-gray-500">No equipment features available</div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedEquipment).map(([mainCategory, subCategories]) => (
        <Card key={mainCategory}>
          <CardContent className="p-6">
            <h3 className="text-lg font-medium mb-4">{mainCategory}</h3>
            
            <Accordion type="multiple" className="w-full">
              {Object.entries(subCategories).map(([category, items], index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-base">
                    {category}
                    <Badge className="ml-2" variant="outline">
                      {items.length}
                    </Badge>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4">
                      {items.map((item, itemIndex) => (
                        <li key={itemIndex} className="text-sm">
                          {item.name || item.description || item}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default EquipmentFeatures;
